import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { supportCaseAPI } from '../services/api';
import { CASE_TYPES, CASE_PRIORITIES } from '../constants/supportCases';
import { CaseTypeIcon } from '../components/icons';
import './SupportCases.css';

/**
 * Open a new support case (bug / question / feature request).
 * Header + bottom Save/Cancel per the long-form layout convention.
 */
function SupportCaseCreate() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get('type');
  const initialType = CASE_TYPES.some((t) => t.value === requestedType) ? requestedType : 'bug';
  const [type, setType] = useState(initialType);
  const [priority, setPriority] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const listPath = tenantPath('/lists/support-cases');
  const canSave = subject.trim() !== '' && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setError(null);
      const payload = {
        type,
        subject: subject.trim(),
        description: description.trim(),
        priority: priority || undefined,
      };
      const res = await supportCaseAPI.create(payload);
      const created = res.data?.data;
      if (created?.id) {
        navigate(tenantPath(`/lists/support-cases/${created.id}`));
      } else {
        navigate(listPath);
      }
    } catch (err) {
      console.error('Error creating support case:', err);
      setError(err.response?.data?.error || err.message || 'Failed to open case');
      setSaving(false);
    }
  };

  return (
    <div className="support-case-form-page">
      <div className="support-case-form-header">
        <div className="support-case-form-header-left">
          <Link to={listPath} className="support-case-back-link">← Back to cases</Link>
          <h1>New support case</h1>
        </div>
        <div className="support-case-form-header-actions">
          <button type="button" className="btn-save" onClick={handleSave} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to={listPath} className="btn-cancel">Cancel</Link>
        </div>
      </div>

      {error && <div className="support-cases-error" role="alert">{error}</div>}

      <form className="support-case-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="support-case-field">
          <span>Type <span className="req">*</span></span>
          <div className="support-case-type-picker" role="radiogroup" aria-label="Case type">
            {CASE_TYPES.map((t) => (
              <button
                type="button"
                key={t.value}
                role="radio"
                aria-checked={type === t.value}
                className={`support-case-type-card${type === t.value ? ' is-selected' : ''}`}
                onClick={() => setType(t.value)}
              >
                <span className={`support-case-type-card-icon type-${t.value}`}>
                  <CaseTypeIcon type={t.value} size={20} />
                </span>
                <span className="support-case-type-card-label">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <label className="support-case-field">
          <span>Priority</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="">— (unset)</option>
            {CASE_PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>

        <label className="support-case-field">
          <span>Subject <span className="req">*</span></span>
          <input
            type="text"
            value={subject}
            maxLength={200}
            placeholder="Short summary of the issue or request"
            onChange={(e) => setSubject(e.target.value)}
          />
        </label>

        <label className="support-case-field">
          <span>Description</span>
          <textarea
            value={description}
            rows={8}
            placeholder="What happened, what you expected, steps to reproduce, or details of the request…"
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>

        <div className="support-case-form-footer">
          <button type="submit" className="btn-save" disabled={!canSave}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <Link to={listPath} className="btn-cancel">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

export default SupportCaseCreate;
